import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Heart, MessageCircle, Trash2, ExternalLink, Send, Loader2, ShieldAlert } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { 
  deletePost, 
  getComments,
  addComment,
} from '../../lib/services/feedService';
import toast from 'react-hot-toast';
import type { GetFeedResult, PostTagRow, CommentWithAuthor, PostRow } from '../../types/database';
import CommentItem from './CommentItem';

interface PostItemProps {
  post: GetFeedResult;
  onToggleLike: (postId: string, authorId: string | null | undefined) => Promise<void>;
  onPostDeleted?: (postId: string) => void;
  onPostUpdated?: (updatedPost: GetFeedResult) => void;
  onTagClick?: (type: 'game' | 'mechanic' | 'category' | 'theme', value: string, displayName?: string) => void;
  onCommentAdded?: (postId: string) => void;
}

// Helper function to convert YouTube URL to embed URL
const YOUTUBE_REGEX = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/|shorts\/)?)([\w\-]+)(\S+)?$/;

const getYouTubeEmbedUrl = (url: string | null): string | null => {
  if (!url) return null;

  // Usar a regex definida acima para validação inicial
  if (!YOUTUBE_REGEX.test(url)) {
      console.warn('URL fornecida não é do YouTube ou formato inválido:', url);
      return null; // Ignorar URLs que não são do YouTube ou inválidas
  }

  try {
    // Tentar construir URL, tratando casos sem protocolo
    const urlObj = new URL(url.startsWith('//') ? `https:${url}` : url.startsWith('http') ? url : `https://${url}`);
    let videoId: string | null = null;

    // Extrair ID de diferentes formatos
    if (urlObj.hostname.includes('youtube.com')) {
      if (urlObj.pathname === '/watch') {
        videoId = urlObj.searchParams.get('v');
      } else if (urlObj.pathname.startsWith('/embed/')) {
        videoId = urlObj.pathname.split('/embed/')[1]?.split('?')?.[0];
      } else if (urlObj.pathname.startsWith('/shorts/')) {
        videoId = urlObj.pathname.split('/shorts/')[1]?.split('?')?.[0];
      }
    } else if (urlObj.hostname.includes('youtu.be')) {
      videoId = urlObj.pathname.substring(1).split('?')[0];
    }

    if (videoId) {
      const cleanVideoId = videoId.split('&')[0]; // Remove parâmetros extras como &t=
      return `https://www.youtube.com/embed/${cleanVideoId}`;
    }

    console.warn('Não foi possível extrair o ID do vídeo do YouTube da URL:', url);
    return null;

  } catch (e) {
    console.error('URL inválida para embed de vídeo:', url, e);
    return null;
  }
};

const PostItem: React.FC<PostItemProps> = ({
  post,
  onToggleLike,
  onPostDeleted,
  onPostUpdated,
  onTagClick,
  onCommentAdded
}) => {
  const { user, isAdmin } = useAuth();
  const [isLiked, setIsLiked] = useState(post.user_liked || false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // States for comments
  const [comments, setComments] = useState<CommentWithAuthor[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [isModeratingComment, setIsModeratingComment] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [commentsFetched, setCommentsFetched] = useState(false);

  // State for tag visibility
  const INITIAL_VISIBLE_TAGS = 5;
  const [areAllTagsShown, setAreAllTagsShown] = useState(false);

  // Calculate embed URL using the helper
  const embedUrl = useMemo(() => getYouTubeEmbedUrl(post.video_url), [post.video_url]);

  // Sanitizar o conteúdo do post antes de renderizar
  const sanitizedContent = useMemo(() => {
    // Permitir apenas tags seguras básicas e forçar target="_blank" em links
    return post.content_text ? DOMPurify.sanitize(post.content_text, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] }) : '';
  }, [post.content_text]);

  const fetchCommentsForPost = useCallback(async () => {
    setLoadingComments(true);
    setCommentError(null);
    try {
      const fetchedComments = await getComments(post.id);
      setComments(fetchedComments || []);
    } catch (error) {
      console.error('Erro ao buscar comentários:', error);
      setCommentError('Não foi possível carregar os comentários.');
      setComments([]);
    } finally {
      setLoadingComments(false);
      setCommentsFetched(true);
    }
  }, [post.id]);

  // Fetch comments when showComments is true and comments haven't been fetched yet
  useEffect(() => {
    if (showComments && !commentsFetched && !loadingComments) {
      fetchCommentsForPost();
    }
  }, [showComments, commentsFetched, loadingComments, fetchCommentsForPost]);

  // Formatar a data de criação com verificação de valor válido
  const formattedDate = React.useMemo(() => {
    try {
      if (!post.created_at) return 'Data desconhecida';
      
      const date = new Date(post.created_at);
      if (isNaN(date.getTime())) return 'Data inválida';
      
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale: ptBR
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data indisponível';
    }
  }, [post.created_at]);

  // Verificar se o usuário atual é o autor do post
  const isAuthor = user && user.id === post.user_id;

  // Manipular like/unlike
  const handleToggleLike = async () => {
    if (!user) {
      toast.error('Você precisa estar logado para curtir um post.');
      return;
    }

    // Backup state for revert
    const previousIsLiked = isLiked;
    const previousLikeCount = likeCount;

    // Optimistic update
    const newIsLiked = !isLiked;
    setIsLiked(newIsLiked);
    setLikeCount(prev => newIsLiked ? prev + 1 : Math.max(0, prev - 1));

    try {
      // Call the parent function passed via prop
      await onToggleLike(post.id, post.user_id); 
      // No need to notify parent again, it's the one doing the work
      
    } catch (error) {
      console.error('Erro ao processar like:', error);
      toast.error('Não foi possível processar sua curtida.');
      
      // Revert optimistic update on failure
      setIsLiked(previousIsLiked);
      setLikeCount(previousLikeCount);
    }
  };

  // Handle add comment
  const handleAddComment = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!user || !newComment.trim() || isPostingComment || isModeratingComment) return;

      setIsPostingComment(true);
      setIsModeratingComment(true);
      toast.loading('Verificando seu comentário...', { id: 'comment-moderation-toast' });

      try {
          // 1. Moderação de Texto
          const { data: textModerationResult, error: textModerationError } = await supabase.functions.invoke('moderate-text', {
            body: { textContent: newComment.trim() }
          });

          toast.dismiss('comment-moderation-toast');
          setIsModeratingComment(false);

          if (textModerationError) {
            console.warn('[PostItem] Erro crítico ao invocar a função de moderação de texto do comentário:', textModerationError);
            toast.error(textModerationError.message || 'Falha crítica ao verificar o comentário.', { duration: 7000 });
            setIsPostingComment(false);
            return;
          }

          if (textModerationResult && !textModerationResult.success) {
            const detailedErrorMessage = textModerationResult.error || 'Conteúdo do comentário considerado impróprio.';
            console.warn(`[PostItem] Moderação de texto do comentário falhou: ${detailedErrorMessage}`);
            toast.error(detailedErrorMessage, { duration: 7000 });
            setIsPostingComment(false);
            return;
          }
          
          if (textModerationResult && textModerationResult.success) {
            // Toast opcional para sucesso da moderação, pode ser removido se for muito verboso
            // toast.success(textModerationResult.message || 'Comentário verificado!', { duration: 2000 });
          }

          // 2. Adicionar Comentário (somente se a moderação passar)
          const payload = {
              post_id: post.id,
              user_id: user.id,
              content: newComment.trim()
          };
          const createdCommentPostRow = await addComment(payload);
          
          const optimisticComment: CommentWithAuthor = {
            id: createdCommentPostRow.id, 
            user_id: user.id,
            content: newComment.trim(),
            created_at: new Date().toISOString(), 
            parent_post_id: post.id,
            author_profile: {
              username: user.username || null,
              avatar_url: user.avatar_url || null,
              full_name: user.full_name || null,
            }
          };
          
          setComments(prev => [...prev, optimisticComment]);
          setNewComment('');
          setCommentCount(prev => prev + 1); 
          if (onCommentAdded) {
              onCommentAdded(post.id); 
          }
          toast.success('Comentário adicionado!');
      } catch (error) { // Captura erros da moderação ou do addComment
          console.error('Erro ao adicionar comentário ou durante moderação:', error);
          toast.error('Não foi possível adicionar seu comentário.');
          // Se o erro foi na moderação e não foi pego antes, o toast de loading pode ainda estar ativo
          toast.dismiss('comment-moderation-toast');
      } finally {
          setIsPostingComment(false);
          setIsModeratingComment(false); // Garante que o estado de moderação seja resetado
      }
  };
  
  // Handle comment deletion (called from CommentItem)
  const handleCommentDeleted = (deletedCommentId: string) => {
      setComments(prev => prev.filter(comment => comment.id !== deletedCommentId));
      setCommentCount(prev => Math.max(0, prev - 1)); // Decrement local count
      // Parent (FeedComponent) needs to be notified to update its count too
      if (onPostUpdated) { // Re-use onPostUpdated for simplicity or add onCommentDeletedCountUpdate
        onPostUpdated({
          ...post,
          comment_count: Math.max(0, commentCount - 1) 
        });
      } 
  };

  // Manipular exclusão do post
  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir este post?')) {
      return;
    }

    setIsDeleting(true);
    try {
      // Passar o status 'isAdmin' para a função deletePost
      const success = await deletePost(post.id, isAdmin);
      if (success) {
        toast.success('Post excluído com sucesso!');
        if (onPostDeleted) {
          onPostDeleted(post.id);
        }
      } else {
        // O toast de erro já deve ter sido mostrado por deletePost em caso de falha na RPC ou RLS
        // Se deletePost não mostrar um toast em caso de erro, você pode adicionar um aqui.
        // toast.error('Não foi possível excluir o post.');
      }
    } catch (error) {
      console.error('Erro ao excluir post:', error);
      toast.error('Erro ao tentar excluir o post.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Renderizar marcadores de jogos/categorias/mecânicas
  const renderTags = () => {
    if (!post.tags || post.tags.length === 0) {
      return null;
    }

    const tagsToDisplay = areAllTagsShown ? post.tags : post.tags.slice(0, INITIAL_VISIBLE_TAGS);
    const hiddenTagsCount = post.tags.length - INITIAL_VISIBLE_TAGS;

    return (
      <div className="flex flex-wrap items-center mt-3">
        {tagsToDisplay.map((tag: PostTagRow, index: number) => {
          let tagClass = '';
          let emoji = '';
          
          // Definir classes e emoji conforme o tipo de tag
          switch (tag.tag_type) {
            case 'game':
              tagClass = 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-600';
              emoji = '🎮';
              break;
            case 'category':
              tagClass = 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-700 dark:text-blue-200 dark:hover:bg-blue-600';
              emoji = '📋';
              break;
            case 'mechanic':
              tagClass = 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-700 dark:text-green-200 dark:hover:bg-green-600';
              emoji = '⚙️';
              break;
            case 'theme':
              tagClass = 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-600 dark:text-yellow-100 dark:hover:bg-yellow-500';
              emoji = '🎭';
              break;
            default:
              tagClass = 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500';
              emoji = '🏷️';
          }
          
          // Valor a ser mostrado no botão
          const displayValue = tag.tag_type === 'game' ? (tag.game_name || tag.game_id || '') : tag.tag_name || '';
          // Valor a ser enviado no onClick
          const clickValue = tag.tag_type === 'game' ? tag.game_id : tag.tag_name;

          // Pular se não houver valor para exibir OU valor para clicar
          if (!displayValue || !clickValue) return null;
          
          // Cria um identificador único para a tag
          const tagKey = `${tag.tag_type}-${tag.id || clickValue}-${index}`;
          
          return (
            <button
              key={tagKey}
              onClick={() => {
                console.log('[PostItem] Tag Clicked - Type:', tag.tag_type, 'Value:', clickValue); // Usar clickValue no log
                if (onTagClick) {
                  // Passar displayValue (game_name ou tag_name) como terceiro argumento, ou undefined se for nulo
                  const displayNameForClick = tag.tag_type === 'game' ? (tag.game_name || undefined) : (tag.tag_name || undefined);
                  onTagClick(tag.tag_type, clickValue, displayNameForClick);
                }
              }}
              className={`inline-flex items-center px-2.5 py-0.5 mr-2 mb-2 rounded-full text-xs font-medium ${tagClass} transition-colors`}
            >
              <span className="mr-1">{emoji}</span>
              <span className="truncate max-w-[100px] sm:max-w-[150px]">{displayValue}</span> {/* Exibir displayValue com truncamento */}
            </button>
          );
        })}
        {post.tags.length > INITIAL_VISIBLE_TAGS && !areAllTagsShown && (
          <button
            onClick={() => setAreAllTagsShown(true)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 ml-1 mb-2 px-2 py-0.5 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900 transition-colors"
          >
            Ver mais (+{hiddenTagsCount})
          </button>
        )}
        {areAllTagsShown && post.tags.length > INITIAL_VISIBLE_TAGS && (
          <button
            onClick={() => setAreAllTagsShown(false)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 ml-1 mb-2 px-2 py-0.5 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900 transition-colors"
          >
            Ver menos
          </button>
        )}
      </div>
    );
  };

  return (
    <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 mb-6 dark:bg-gray-800 dark:border-gray-700 dark:hover:shadow-gray-700/50">
      {/* Cabeçalho do post */}
      <div className="p-4 flex items-start">
        {post.author_username ? (
          <Link to={`/profile/${post.author_username}`} className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg overflow-hidden dark:bg-indigo-500 dark:text-indigo-100">
              {post.author_avatar_url ? (
                <img
                  src={post.author_avatar_url}
                  alt={post.author_username}
                  className="w-full h-full object-cover"
                />
              ) : (
                (post.author_username || 'U').charAt(0).toUpperCase()
              )}
            </div>
          </Link>
        ) : (
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-lg overflow-hidden dark:bg-gray-700 dark:text-gray-300">
              {(post.author_username || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        <div className="ml-3 flex-1">
          <div className="flex items-center justify-between">
            <div>
              {post.author_username ? (
                <Link 
                  to={`/profile/${post.author_username}`}
                  className="font-medium text-gray-900 hover:text-indigo-600 transition-colors dark:text-gray-100 dark:hover:text-indigo-400"
                >
                  {post.author_username}
                </Link>
              ) : (
                <span className="font-medium text-gray-900 dark:text-gray-100">Usuário</span>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">{formattedDate}</p>
            </div>
            {(isAuthor || isAdmin) && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-gray-100 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-gray-700"
                aria-label="Excluir post"
              >
                {isDeleting ? (
                  <div className="w-5 h-5 border-2 border-t-2 border-gray-500 rounded-full animate-spin dark:border-gray-400" />
                ) : (
                  <Trash2 size={18} />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal do post */}
      <div className="px-4 pb-3">
        {/* Usar dangerouslySetInnerHTML com o conteúdo sanitizado */}
        {sanitizedContent && (
          <div 
            className="text-gray-800 whitespace-pre-line mb-3 prose prose-sm max-w-none dark:text-gray-200" 
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}
        {post.image_url && (
          <div className="mb-3 rounded-lg overflow-hidden dark:border dark:border-gray-700">
            <img
              src={post.image_url}
              alt="Conteúdo do post"
              className="w-full object-cover max-h-96"
              loading="lazy"
            />
          </div>
        )}
        {/* Vídeo do post - Use embedUrl */} 
        {embedUrl && (
          <div className="mb-3 rounded-lg overflow-hidden aspect-video dark:border dark:border-gray-700">
            <iframe
              src={embedUrl}
              title="Vídeo do post"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}
        {renderTags()}
      </div>
      
      {/* Barra de ações */}
      <div className="px-4 py-3 border-t border-gray-100  flex items-center justify-between dark:bg-gray-750 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleToggleLike}
            className={`flex items-center text-sm font-medium ${isLiked ? 'text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300' : 'text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100'} transition-colors`}
            aria-label={isLiked ? 'Descurtir' : 'Curtir'}
          >
            <Heart size={18} className={`mr-1 ${isLiked ? 'fill-current' : ''}`} />
            <span className="dark:text-gray-200">{likeCount > 0 ? likeCount : ''} {likeCount === 1 ? 'Curtida' : 'Curtidas'}</span>
          </button>
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
            aria-label={showComments ? 'Ocultar comentários' : 'Ver comentários'}
          >
            <MessageCircle size={18} className="mr-1" />
            <span className="dark:text-gray-200">{commentCount} {commentCount === 1 ? 'Comentário' : 'Comentários'}</span>
          </button>
        </div>
        <Link 
          to={`/post/${post.id}`}
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          <ExternalLink size={14} className="mr-1" />
          Ver detalhes
        </Link>
      </div>
      
      {/* Seção de Comentários (Condicional) */}
      {showComments && (
        <div className="border-t border-gray-100 p-4 dark:bg-gray-750 dark:border-gray-700">
          {/* Formulário para adicionar comentário */} 
          {user && (
            <form onSubmit={handleAddComment} className="flex items-start mb-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escreva um comentário..."
                className="flex-grow p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:border-gray-600"
                disabled={!user || isPostingComment || isModeratingComment}
              />
              <button 
                type="submit" 
                disabled={!user || !newComment.trim() || isPostingComment || isModeratingComment}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r-md disabled:opacity-50 flex items-center dark:bg-blue-600 dark:hover:bg-blue-500"
              >
                {isModeratingComment ? (
                  <ShieldAlert size={18} className="mr-1 animate-pulse" />
                ) : isPostingComment ? (
                  <Loader2 size={18} className="mr-1 animate-spin" />
                ) : (
                  <Send size={18} className="mr-1" />
                )}
                {isModeratingComment ? 'Verificando...' : isPostingComment ? 'Enviando...' : 'Enviar'}
              </button>
            </form>
          )}
          
          {/* Lista de Comentários */}
          {loadingComments ? (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto dark:text-indigo-400" />
            </div>
          ) : commentError ? (
            <p className="text-center text-red-500 text-sm py-4 dark:text-red-400">{commentError}</p>
          ) : comments.length > 0 ? (
            <div className="space-y-2">
              {comments.map(comment => (
                <CommentItem 
                  key={comment.id} 
                  comment={comment} 
                  onCommentDeleted={handleCommentDeleted}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 text-sm py-4 dark:text-gray-400">Nenhum comentário ainda.</p>
          )}
        </div>
      )}
    </article>
  );
};

export default PostItem;